/* eslint-disable filename-rules/match */
import { mean } from "lodash-es";
import { useEffect } from "react";

import { SubmissionType } from "components/server-types";
import { storeApi } from "store/index";
import {
  assessmentSelectors,
  outcomeAssessmentSelectors,
  pageGroupUserSelectors,
  pageOutcomeSelectors,
  pageSelectors,
  sectionOutcomeSelectors,
  sectionUserSelectors,
  submissionSelectors,
} from "store/selectors";
import { useFullObjectSelector, useListSelector } from "store/store-hooks";
import { filterMap } from "utils/collections";
import { isGroupPage } from "utils/page-utils";

/**
 * Returns a map of outcome IDs to their average scores for a given user, aggregated across all
 * assessed pages across all sections the user is enrolled in.
 */
export const useStudentAverageOutcomeScores = (userId: string | null): Record<string, number> => {
  useEffect(() => {
    // Note we don't need to pass in student ids for everything because the backend already
    // filters for only data the student can see.
    if (userId) {
      storeApi.page_group_users.list({ section_user__user_id: userId });
      storeApi.page_outcomes.list();
      storeApi.pages.list();
      storeApi.submissions.list();
      storeApi.assessments.list();
      storeApi.outcome_assessments.list();
    }
  }, [userId]);

  const sectionUsersForUser = useListSelector(
    (s) => sectionUserSelectors.filterByUserId(s, userId),
    ["id", "section_id"],
  );
  const sectionUserIdsForUser = new Set(sectionUsersForUser.map((cu) => cu.id));
  const sectionIdsForUser = new Set(sectionUsersForUser.map((cu) => cu.section_id));

  const allPageGroupUsers = useListSelector(pageGroupUserSelectors.selectAll, [
    "page_group_id",
    "section_user_id",
  ]);
  const pageGroupIdsForUser = new Set(
    filterMap(
      allPageGroupUsers,
      (pgu) => sectionUserIdsForUser.has(pgu.section_user_id),
      (pgu) => pgu.page_group_id,
    ),
  );
  const allPages = useListSelector(pageSelectors.selectAll, [
    "id",
    "interaction_mode",
    "section_id",
    "released_at",
    "assessments_published_at",
  ]);
  const assessedPagesForUser = allPages.filter(
    (p) => sectionIdsForUser.has(p.section_id) && p.released_at && p.assessments_published_at,
  );

  // Entity map; only keyed lookups by section_outcome_id.
  const sectionOutcomeMap = useFullObjectSelector(sectionOutcomeSelectors.selectEntities);
  const allPageOutcomes = useListSelector(pageOutcomeSelectors.selectAll, [
    "id",
    "page_id",
    "section_outcome_id",
  ]);
  const allSubmissions = useListSelector(submissionSelectors.selectAll, [
    "id",
    "page_id",
    "official_at",
    "section_user_id",
    "page_group_id",
  ]);
  const allAssessments = useListSelector(assessmentSelectors.selectAll, ["id", "submission_id"]);
  const allOutcomeAssessments = useListSelector(outcomeAssessmentSelectors.selectAll, [
    "id",
    "page_outcome_id",
    "assessment_id",
    "score",
  ]);

  const aggregatedScoresByOutcomeId = assessedPagesForUser.reduce(
    (acc, page) => {
      const isSubmissionForUser = isGroupPage(page)
        ? (submission: Pick<SubmissionType, "page_group_id">) =>
            pageGroupIdsForUser.has(submission.page_group_id!)
        : (submission: Pick<SubmissionType, "section_user_id">) =>
            sectionUserIdsForUser.has(submission.section_user_id!);
      const officialSubmission = allSubmissions.find(
        (submission) =>
          submission.page_id === page.id &&
          submission.official_at &&
          isSubmissionForUser(submission),
      );
      if (!officialSubmission) {
        return acc;
      }

      const assessment = allAssessments.find((a) => a.submission_id === officialSubmission.id);
      if (!assessment) {
        return acc;
      }

      const pageOutcomesForPage = allPageOutcomes.filter((po) => po.page_id === page.id);
      pageOutcomesForPage.forEach((pageOutcome) => {
        const sectionOutcome = sectionOutcomeMap[pageOutcome.section_outcome_id];
        if (!sectionOutcome) {
          return;
        }
        const outcomeId = sectionOutcome.outcome_id;

        const outcomeAssessment = allOutcomeAssessments.find(
          (oa) => oa.page_outcome_id === pageOutcome.id && oa.assessment_id === assessment.id,
        );
        if (!outcomeAssessment) {
          return;
        }

        if (!acc[outcomeId]) {
          // eslint-disable-next-line no-param-reassign
          acc[outcomeId] = [];
        }
        acc[outcomeId].push(outcomeAssessment.score);
      });

      return acc;
    },
    {} as Record<string, number[]>,
  );

  // Now that we have aggregated scores, calculate the average for each outcome
  return Object.fromEntries(
    Object.entries(aggregatedScoresByOutcomeId).map(([outcomeId, scores]) => {
      const averageScore = mean(scores);
      return [outcomeId, averageScore];
    }),
  );
};
